import React, { useCallback, useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import omit from 'lodash.omit';
import toast from 'react-hot-toast';
import cx from 'classnames';
import { IoIosArrowRoundBack } from 'react-icons/io';
import {
  useCreateUserEventMutation,
  usePatchEventMutation,
  useGetEventQuery,
  useDeleteEventDocumentMutation,
} from '@@/services/event';
import { useGetPopulatedPatientQuery } from '@@/services/user';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import EventForm, { schema } from '@@/components/Forms/EventForm';
import { getPractitionerContactLabel } from '@@/components/Inputs/SelectPractitionerContactInput';
import { translateRecurringFrequency } from '@@/constants/recurringEvent';
import { conversationUrlFor } from '@@/utils/conversation';

export type EventFormType = {
  name: string;
  notes?: string;
  location?: string;
  targetPractitioner?: {
    id: string;
    label: string;
  };
  startDate: Date;
  endDate: Date;
  shouldSendCalendarInvite: boolean;
  shouldSendScheduledMessage?: boolean;
  scheduledMessageDate?: Date;
  scheduledMessageBody?: Date;
  recurringFrequency?: {
    id: string;
    label: string;
  };
};

const NEW_EVENT = 'new';

const EventPage: React.FC = () => {
  const { patientId, eventId } = useParams();
  const navigate = useNavigate();
  const [createEvent, { isSuccess }] = useCreateUserEventMutation();

  const [patchEvent, { isSuccess: isPatchSuccess }] = usePatchEventMutation();
  const [deleteDocument, { isSuccess: isDocumentDeletionSuccess }] =
    useDeleteEventDocumentMutation();

  const isNewEvent = useMemo(() => eventId === NEW_EVENT, [eventId]);

  const { data: event } = useGetEventQuery(eventId || '', {
    skip: !eventId || isNewEvent,
  });

  const { data: patient } = useGetPopulatedPatientQuery(patientId || '', {
    skip: !patientId,
  });

  const backLink = useMemo(
    () => conversationUrlFor(patient, '#preview@evenements-cles'),
    [patient],
  );

  const form = useForm<EventFormType & { attachments: File[] }>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });

  const onDeleteDocument = useCallback(
    (attachmentId: string) => {
      if (event?.id) {
        deleteDocument({ eventId: event.id, attachmentId });
      }
    },
    [deleteDocument, event],
  );

  const onSubmit = useCallback(
    async (data: EventFormType & { attachments: File[] }) => {
      if (patientId) {
        const { attachments, ...body } = data;
        if (event?.id) {
          patchEvent({
            eventId: event.id,
            body: {
              ...body,
              targetPractitioner: body.targetPractitioner?.id,
              recurringFrequency: body.recurringFrequency?.id,
            },
            attachments,
          });
        } else {
          createEvent({
            userId: patientId,
            body: {
              ...body,
              targetPractitioner: body.targetPractitioner?.id,
              recurringFrequency: body.recurringFrequency?.id,
            },
            attachments,
          });
        }
      }
    },
    [createEvent, event?.id, patientId],
  );

  useEffect(() => {
    if (event?.id) {
      const data = omit(event, [
        '_id',
        'id',
        'createdAt',
        'updatedAt',
        'userId',
        'attachmentIds',
        'calendlyId',
        'doctolibId',
        'targetPractitioner',
        'doctolibPractitionerName',
        'isDoctolibOrganization',
        'doctolibId',
        'isNoShow',
        'recurringEvent',
        'url',
        'oneHourReminderSentAt',
        'oneDayReminderSentAt',
      ]) as EventFormType;

      if (event?.targetPractitioner) {
        data.targetPractitioner = {
          id: event.targetPractitioner.id,
          label: getPractitionerContactLabel(event.targetPractitioner),
        };
      }

      if (event?.recurringEvent) {
        data.recurringFrequency = {
          id: event.recurringEvent?.frequency,
          label: translateRecurringFrequency(event.recurringEvent?.frequency),
        };
      }

      form.reset(data);
    }
  }, [event?.id, form.reset]);

  useEffect(() => {
    if (isSuccess) {
      toast.success(`Votre évènement a bien été créé`, {
        position: 'top-right',
      });

      navigate(backLink);
    }
  }, [isSuccess, backLink, navigate]);

  useEffect(() => {
    if (isPatchSuccess) {
      toast.success(`Votre évènement a bien été modifié`, {
        position: 'top-right',
      });

      navigate(backLink);
    }
  }, [isPatchSuccess, backLink, navigate]);

  useEffect(() => {
    if (isDocumentDeletionSuccess) {
      toast.success(`La pièce jointe a bien été supprimée`, {
        position: 'top-right',
      });
    }
  }, [isDocumentDeletionSuccess]);

  return (
    <div className="px-4 w-full">
      <Link className="p-2 my-2 flex flex-row justify-start" to={backLink}>
        <IoIosArrowRoundBack size={20} />
        <span className="ml-1">Retour</span>
      </Link>

      <EventForm
        form={form}
        birthDate={patient?.medicalRecord?.birthDate}
        existingAttachments={event?.attachmentIds}
        onDeleteDocument={onDeleteDocument}
        isDoctolib={!!event?.doctolibId}
      />

      <button
        type="button"
        onClick={form.handleSubmit(onSubmit)}
        disabled={form.formState.isSubmitting}
        className={cx(
          'my-2 rounded-lg p-2 bg-sky-600 ml-auto block',
          form.formState.isSubmitting && 'opacity-50 cursor-not-allowed',
        )}
      >
        <span className="text-white">Valider</span>
      </button>
    </div>
  );
};

export default EventPage;
